import React, {useState} from 'react';

import {Button, Modal, MultiSelect, TextArea} from '@/components';
import type {BusinessRFI} from '@/types';

import {getReuploadableDocuments} from './rfiStatus';

type RequestReuploadModalProps = {
  rfi: BusinessRFI;
  isOpen: boolean;
  isLoading: boolean;
  dataCy?: string;
  onClose: () => void;
  onSubmit: (note: string, documentsToReupload: string[]) => void;
};

const RequestReuploadModal = ({
  rfi,
  isOpen,
  isLoading,
  dataCy,
  onClose,
  onSubmit,
}: RequestReuploadModalProps) => {
  // Constrain to docs the business has already uploaded (fallback to all requested).
  const choices = getReuploadableDocuments(rfi);

  // `selected` is the comma-joined MultiSelect value.
  const [selected, setSelected] = useState<string>('');
  const [note, setNote] = useState('');

  const selectedDocs = selected.split(',').filter(Boolean);
  const canSubmit = selectedDocs.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      dataCy={dataCy ? `${dataCy}-reupload-documents` : undefined}
      onClose={onClose}
      title="Request Re-upload"
      width="w-10/12 sm:w-10/12 md:w-7/12 lg:w-4/12"
      closeOnOutsideClick
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
            Documents to re-upload
          </label>
          <MultiSelect
            dataCy={dataCy ? `${dataCy}-reupload-documents` : undefined}
            id="rfiReuploadDocs"
            name="rfiReuploadDocs"
            options={choices.map((doc) => ({value: doc, label: doc}))}
            value={selected}
            onChange={setSelected}
            placeholder="--Select document(s)--"
          />
        </div>

        <TextArea
          data-cy={dataCy ? `${dataCy}-reupload-documents-textarea` : undefined}
          label="Reason (optional)"
          value={note}
          placeholder="Why does the business need to re-upload these?"
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setNote(e.target.value)
          }
        />

        <div className="flex justify-end gap-3">
          <Button
            dataCy={dataCy ? `${dataCy}-reupload-cancel` : undefined}
            variant="ghost"
            colorScheme="cyan"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            dataCy={dataCy ? `${dataCy}-reupload-confirm` : undefined}
            colorScheme="cyan"
            onClick={() => onSubmit(note, selectedDocs)}
            isLoading={isLoading}
            loadingText="Requesting..."
            disabled={!canSubmit}
          >
            Request Re-upload
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RequestReuploadModal;
